import { useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/Button';
import { CompanySelectBase } from '@/features/agent/provided_job_posts/components/CompanySelect';
import useMergeCompany from '../api/useMergeCompany';
import { confirm } from '@/utils/confirm';
import Loading from '@/components/Loading';
import { useNavigate } from '@tanstack/react-router';
import { showErrorToast } from '../../job_change_processes/lib/toast';

interface CompanyMergeModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: number;
  companyName: string;
}

export default function CompanyMergeModal({
  isOpen,
  onClose,
  companyId,
  companyName,
}: CompanyMergeModalProps) {
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [selectedCompanyName, setSelectedCompanyName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const mergeCompany = useMergeCompany();
  const navigate = useNavigate();

  const handleMerge = async () => {
    if (!selectedCompanyId) {
      showErrorToast('統合先の企業を選択してください');
      return;
    }

    const message = `「${companyName}」を「${selectedCompanyName}」に統合しますか？求人・マッチング・面接ログを含むデータが移行されます。この操作は取り消せません。統合後、「${companyName}」は削除されます。`;

    const confirmed = await confirm({
      title: '企業統合の確認',
      message: message,
    });

    if (!confirmed) {
      return;
    }

    setIsLoading(true);
    try {
      const result = await mergeCompany.mutateAsync({
        companyId,
        targetCompanyId: selectedCompanyId,
      });

      // 統合先企業の詳細ページにリダイレクト
      navigate({
        to: '/agent/companies/$companiesId',
        params: { companiesId: result.target_company_id.toString() },
      });
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : '統合に失敗しました');
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setSelectedCompanyId(null);
      setSelectedCompanyName('');
      onClose();
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleClose();
      }}
    >
      <DialogContent
        className="w-full max-w-screen-sm rounded shadow-xl!"
        onInteractOutside={(event) => {
          if (isLoading) event.preventDefault();
        }}
        onEscapeKeyDown={(event) => {
          if (isLoading) event.preventDefault();
        }}
      >
        {/* ヘッダー */}
        <div className="flex items-center justify-between border-b border-solid border-gray-200 px-6 py-4">
          <DialogTitle className="text-base! font-bold! text-gray-900">
            他の企業へ統合する
          </DialogTitle>
        </div>

        {/* コンテンツ */}
        <div className="p-6">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center gap-4 py-8">
              <Loading size="medium" />
              <p className="text-sm text-gray-600">統合処理中です...</p>
            </div>
          ) : (
            <>
              <p className="mb-4 text-sm text-gray-900">
                統合先の企業を選択してください。選択した企業に「{companyName}
                」のデータが統合されます。
              </p>
              <div className="flex flex-col gap-2">
                <label className="text-sm font-bold text-gray-900">統合先企業</label>
                <CompanySelectBase
                  value={selectedCompanyId ? [selectedCompanyId] : []}
                  onChange={(companyIds) => setSelectedCompanyId(companyIds[0] || null)}
                  onSelectName={(name) => setSelectedCompanyName(name)}
                  placeholder="企業名を入力して検索"
                />
              </div>
            </>
          )}
        </div>

        {/* フッター */}
        {!isLoading && (
          <div className="flex justify-end gap-4 border-t border-solid border-gray-200 px-6 py-4">
            <Button color="secondary" size="md" onClick={handleClose}>
              キャンセル
            </Button>
            <Button color="danger" size="md" onClick={handleMerge} disabled={!selectedCompanyId}>
              統合する
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
